import {
  customElement,
  type TemplateResult,
  property,
  html,
  css,
  cssManager,
} from '@design.estate/dees-element';
import * as domtools from '@design.estate/dees-domtools';
import { DeesInputBase } from '../dees-input-base/dees-input-base.js';
import { demoFunc } from './dees-input-toggle.demo.js';
import { themeDefaultStyles } from '../../00theme.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-input-toggle': DeesInputToggle;
  }
}

@customElement('dees-input-toggle')
export class DeesInputToggle extends DeesInputBase<DeesInputToggle> {
  // STATIC
  public static demo = demoFunc;
  public static demoGroups = ['Input'];

  // INSTANCE

  @property({ type: Boolean, reflect: true })
  accessor value: boolean = false;

  /**
   * Knob position tracking (0 = off, maxTravel = on)
   */
  private currentX = 0;

  /**
   * Drag state
   */
  private isDragging = false;
  private hasDragged = false;
  private startX = 0;

  // Toggle dimensions (compact track; matches dense control rows)
  private readonly trackWidth = 36;
  private readonly trackHeight = 20;
  private readonly knobSize = 14;
  private readonly padding = 2;
  private readonly maxTravel = 16; // trackWidth - knobSize - (padding * 2) - border

  constructor() {
    super();
    this.labelPosition = 'right'; // Toggle defaults to label on the right
  }

  public static styles = [
    themeDefaultStyles,
    ...DeesInputBase.baseStyles,
    cssManager.defaultStyles,
    css`
      * {
        box-sizing: border-box;
      }

      :host {
        position: relative;
        cursor: default;
        font-family: var(--dees-font-family);
      }

      .maincontainer {
        display: inline-flex;
        align-items: flex-start;
        gap: var(--dees-spacing-sm);
        cursor: pointer;
        user-select: none;
        transition: all var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .toggle-track {
        position: relative;
        flex-shrink: 0;
        height: 20px;
        width: 36px;
        border-radius: var(--dees-radius-full);
        background: var(--dees-color-fill-secondary);
        border: 1px solid var(--dees-color-border-subtle);
        overflow: visible;
        transition: all var(--dees-transition-fast) var(--dees-ease-standard);
        margin-top: 0px;
      }

      .maincontainer:hover .toggle-track {
        border-color: var(--dees-color-border-strong);
      }

      :host([value]) .toggle-track {
        background: var(--dees-color-accent-success);
        border-color: var(--dees-color-accent-success);
      }

      .toggle-track:focus-visible {
        outline: none;
        box-shadow: 0 0 0 3px var(--dees-color-focus-ring);
      }

      .toggle-knob {
        position: absolute;
        top: 2px;
        width: 14px;
        height: 14px;
        border-radius: 50%;
        background: var(--dees-color-control-thumb);
        box-shadow: var(--dees-shadow-control-thumb);
        transition: left var(--dees-transition-default) var(--dees-ease-snappy), background var(--dees-transition-fast) var(--dees-ease-standard);
        touch-action: none;
      }

      .toggle-knob.dragging {
        transition: background var(--dees-transition-fast) var(--dees-ease-standard);
      }

      :host([value]) .toggle-knob {
        background: var(--dees-color-on-accent);
      }

      /* Disabled state */
      .maincontainer.disabled {
        cursor: not-allowed;
        opacity: 0.5;
      }

      .toggle-track.disabled {
        background: var(--dees-color-badge-default-bg);
        border-color: var(--dees-color-border-default);
      }

      /* Required state (locked on) */
      :host([required][value]) .toggle-track {
        background: var(--dees-color-accent-success);
        border-color: var(--dees-color-accent-success);
        cursor: not-allowed;
      }

      :host([required][value]) .toggle-knob {
        background: color-mix(in srgb, var(--dees-color-on-accent) 78%, transparent);
      }

      /* Label */
      .label-container {
        display: flex;
        flex-direction: column;
        gap: 2px;
        flex: 1;
      }

      .toggle-label {
        font-size: var(--dees-font-control-size);
        font-weight: var(--dees-font-control-weight);
        line-height: var(--dees-font-subhead-line);
        color: var(--dees-color-text-secondary);
        transition: color var(--dees-transition-fast) var(--dees-ease-standard);
        letter-spacing: -0.01em;
      }

      .maincontainer:hover .toggle-label {
        color: var(--dees-color-text-primary);
      }

      .maincontainer.disabled:hover .toggle-label {
        color: var(--dees-color-text-secondary);
      }

      /* Label-left row layout (grouped forms / settings rows): the whole row
         spans full width and the switch sits at the trailing edge. Override the
         base grid, which would otherwise pin the switch inside the label column. */
      :host([label-position="left"]) .input-wrapper {
        display: block;
      }
      :host([label-position="left"]) .maincontainer {
        display: flex;
        width: 100%;
        align-items: center;
      }
      :host([label-position="left"]) .label-container {
        order: -1;
        flex: 1;
      }
      /* In a settings row the switch label reads as primary, like the other
         row labels (not the muted standalone-toggle treatment). */
      :host([label-position="left"]) .toggle-label {
        color: var(--dees-color-text-primary);
      }

    `,
  ];

  public render(): TemplateResult {
    return html`
      <div class="input-wrapper">
        <div class="maincontainer ${this.disabled ? 'disabled' : ''}" @click="${this.handleClick}">
          <div
            class="toggle-track ${this.disabled ? 'disabled' : ''}"
            tabindex="${this.disabled ? '-1' : '0'}"
            @keydown="${this.handleKeydown}"
          >
            <div
              class="toggle-knob"
              style="left: ${this.padding + this.currentX}px;"
              @pointerdown="${this.onPointerDown}"
              @pointermove="${this.onPointerMove}"
              @pointerup="${this.onPointerUp}"
              @pointercancel="${this.onPointerUp}"
            ></div>
          </div>
          <div class="label-container">
            ${this.label ? html`<div class="toggle-label">${this.label}</div>` : ''}
            ${this.renderDescription()}
          </div>
        </div>
      </div>
    `;
  }

  public async firstUpdated(_changedProperties: Map<PropertyKey, unknown>) {
    await super.firstUpdated(_changedProperties);
    // Required toggles are locked on; forcing the value re-runs willUpdate,
    // which positions the knob.
    if (this.required && !this.value) {
      this.value = true;
    }
  }

  /**
   * Keep the knob position in sync with `value` *before* each render, so the
   * very first paint is already correct — no off→on slide on load and a
   * deterministic render. Skipped while dragging or settling a drag, where the
   * pointer handlers own `currentX`.
   */
  protected willUpdate(changedProperties: Map<PropertyKey, unknown>): void {
    super.willUpdate(changedProperties);
    if (!this.isDragging && !this.hasDragged) {
      this.currentX = this.value ? this.maxTravel : 0;
    }
  }

  /**
   * Click handler - toggles the value
   */
  private async handleClick(event: MouseEvent) {
    if (this.isDragging || this.hasDragged) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }

    if (this.disabled) {
      return;
    }

    if (this.required) {
      // Bounce animation for required toggles
      this.currentX = this.maxTravel;
      this.requestUpdate();
      await new Promise((r) => setTimeout(r, 10));
      this.currentX = this.maxTravel - 3;
      this.requestUpdate();
      await domtools.plugins.smartdelay.delayFor(150);
      this.currentX = this.maxTravel;
      this.requestUpdate();
      return;
    }

    event.stopPropagation();
    event.preventDefault();

    this.value = !this.value;
    this.currentX = this.value ? this.maxTravel : 0;
    this.requestUpdate();

    this.dispatchEvent(
      new CustomEvent('newValue', {
        detail: this.value,
        bubbles: true,
      })
    );
    this.changeSubject.next(this);

    domtools.plugins.smartdelay.delayFor(0).then(() => {
      this.hasDragged = false;
    });
  }

  /**
   * Pointer down - start dragging
   */
  private onPointerDown(event: PointerEvent) {
    if (this.required || this.disabled) {
      return;
    }

    this.isDragging = true;
    this.startX = event.clientX - this.currentX;
    (event.target as HTMLElement).setPointerCapture(event.pointerId);
  }

  /**
   * Pointer move - track drag position
   */
  private onPointerMove(event: PointerEvent) {
    if (!this.isDragging) return;
    const newX = event.clientX - this.startX;
    this.hasDragged = true;

    const toggleKnob = this.shadowRoot?.querySelector('.toggle-knob') as HTMLDivElement;
    if (toggleKnob) {
      toggleKnob.classList.add('dragging');
    }

    this.currentX = Math.max(0, Math.min(newX, this.maxTravel));
    this.requestUpdate();
  }

  /**
   * Pointer up - complete drag and snap to nearest side
   */
  private onPointerUp(event: PointerEvent) {
    if (!this.isDragging) return;
    (event.target as HTMLElement).releasePointerCapture(event.pointerId);
    this.isDragging = false;

    if (!this.hasDragged) {
      return;
    }

    const toggleKnob = this.shadowRoot?.querySelector('.toggle-knob') as HTMLDivElement;
    if (toggleKnob) {
      toggleKnob.classList.remove('dragging');
    }

    // Snap to nearest side based on midpoint
    const midpoint = this.maxTravel / 2;
    this.value = this.currentX > midpoint;
    this.currentX = this.value ? this.maxTravel : 0;
    this.requestUpdate();

    this.dispatchEvent(
      new CustomEvent('newValue', {
        detail: this.value,
        bubbles: true,
      })
    );
    this.changeSubject.next(this);

    domtools.plugins.smartdelay.delayFor(0).then(() => {
      this.hasDragged = false;
    });
  }


  /**
   * Keyboard support
   */
  private handleKeydown(event: KeyboardEvent) {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this.handleClick(event as unknown as MouseEvent);
    }
  }

  // DeesInputBase interface implementation
  public getValue(): boolean {
    return this.value;
  }

  public setValue(valueArg: boolean): void {
    this.value = valueArg;
  }

  public focus(): void {
    const track = this.shadowRoot?.querySelector('.toggle-track');
    if (track) {
      (track as HTMLElement).focus();
    }
  }
}
